// Axios client targeting the server-consent service (its own Front Door).
// Auth contract: attach the SSO access token on each request, bounce to
// login on 401.

import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { config } from "../config";
import { getApiAccessToken, login } from "../auth/msal";

export const consentApi = axios.create({
  baseURL: config.consentApiBaseUrl,
  timeout: 60000,
  headers: {
    Accept: "application/json",
  },
});

consentApi.interceptors.request.use(async (req: InternalAxiosRequestConfig) => {
  const token = await getApiAccessToken();
  if (token) {
    req.headers.set("Authorization", `Bearer ${token}`);
  }
  return req;
});

consentApi.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    if (err.response?.status === 401) {
      // Dead session -> back through SSO.
      await login(window.location.pathname);
      return Promise.reject(new Error("session_expired"));
    }
    return Promise.reject(err);
  },
);

// ----- Types -------------------------------------------------------------

export type ConsentKind = "consent" | "acknowledgment";

export type PartyIdentifierType = "sso_oid" | "email" | "phone" | "plate_token";

export interface ConsentDefinitionVersion {
  id: string;
  version: number;
  body: string;
  body_sha256: string;
  requires_reconsent: boolean;
  valid_from: string;
}

export interface ConsentDefinition {
  code: string;
  kind: ConsentKind;
  legal_basis: string;
  blocking: boolean;
  contexts: string[];
  active: boolean;
  versions: ConsentDefinitionVersion[];
}

export interface CreateDefinitionInput {
  code: string;
  kind: ConsentKind;
  legal_basis: string;
  blocking: boolean;
  contexts: string[];
  body: string;
}

export interface CreateVersionInput {
  body: string;
  requires_reconsent: boolean;
}

export interface Party {
  id: string;
  type: PartyIdentifierType;
  status: string;
  created_at: string;
}

export interface PartyConsent {
  definition_code: string;
  action: string;
  version: number;
  occurred_at: string;
  channel: string;
}

export interface PartyLookupResult {
  party: Party;
  consents: PartyConsent[];
}

export interface ConsentEvent {
  id: string;
  definition_code: string;
  action: string;
  version: number;
  occurred_at: string;
  channel: string;
  proof: unknown;
}

// ----- Endpoints ----------------------------------------------------------

// The API wraps the list ({ items }) and serialises legal_basis as legalBasis;
// normalise here so the components see the clean ConsentDefinition shape.
export async function fetchDefinitions(): Promise<ConsentDefinition[]> {
  const { data } = await consentApi.get<{ items?: unknown[] }>("/api/v1/admin/definitions");
  const items = Array.isArray(data?.items) ? data.items : [];
  return items.map((raw) => {
    const d = raw as Record<string, unknown>;
    return {
      code: String(d.code ?? ""),
      kind: d.kind as ConsentKind,
      legal_basis: String(d.legal_basis ?? d.legalBasis ?? ""),
      blocking: Boolean(d.blocking),
      contexts: Array.isArray(d.contexts) ? (d.contexts as string[]) : [],
      active: Boolean(d.active),
      versions: (Array.isArray(d.versions) ? d.versions : []).map((v) => {
        const x = v as Record<string, unknown>;
        return {
          id: String(x.id ?? ""),
          version: Number(x.version),
          body: String(x.body ?? ""),
          body_sha256: String(x.body_sha256 ?? ""),
          requires_reconsent: Boolean(x.requires_reconsent),
          valid_from: String(x.valid_from ?? ""),
        };
      }),
    };
  });
}

export async function createDefinition(input: CreateDefinitionInput): Promise<void> {
  await consentApi.post("/api/v1/admin/definitions", input);
}

export interface UpdateDefinitionInput {
  blocking?: boolean;
  contexts?: string[];
  active?: boolean;
  legal_basis?: string;
}

// Update a definition's mutable metadata (text stays immutable → new version).
export async function updateDefinition(
  code: string,
  input: UpdateDefinitionInput,
): Promise<void> {
  await consentApi.patch(`/api/v1/admin/definitions/${encodeURIComponent(code)}`, input);
}

export interface DeleteDefinitionResult {
  deleted: boolean;
  deactivated: boolean;
}

// Delete a definition: hard delete if never consented, else soft (deactivated).
export async function deleteDefinition(code: string): Promise<DeleteDefinitionResult> {
  const { data } = await consentApi.delete<{ deleted?: boolean; deactivated?: boolean }>(
    `/api/v1/admin/definitions/${encodeURIComponent(code)}`,
  );
  return { deleted: Boolean(data?.deleted), deactivated: Boolean(data?.deactivated) };
}

export async function createDefinitionVersion(
  code: string,
  input: CreateVersionInput,
): Promise<void> {
  await consentApi.post(
    `/api/v1/admin/definitions/${encodeURIComponent(code)}/versions`,
    input,
  );
}

// API returns { party, current:[{definition,…}] }; map to { party, consents:[{definition_code,…}] }.
export async function lookupParty(
  identifier: string,
  type: PartyIdentifierType,
): Promise<PartyLookupResult> {
  const { data } = await consentApi.get<{ party: Party; current?: unknown[] }>(
    "/api/v1/admin/parties",
    { params: { identifier, type } },
  );
  const current = Array.isArray(data?.current) ? data.current : [];
  return {
    party: data.party,
    consents: current.map((c) => {
      const x = c as Record<string, unknown>;
      return {
        definition_code: String(x.definition_code ?? x.definition ?? ""),
        action: String(x.action ?? ""),
        version: Number(x.version),
        occurred_at: String(x.occurred_at ?? ""),
        channel: String(x.channel ?? ""),
      };
    }),
  };
}

// API returns { party_id, events:[{id,definition,…}] }; map to ConsentEvent[].
export async function fetchPartyEvents(partyId: string): Promise<ConsentEvent[]> {
  const { data } = await consentApi.get<{ events?: unknown[] }>(
    `/api/v1/admin/parties/${encodeURIComponent(partyId)}/events`,
  );
  const events = Array.isArray(data?.events) ? data.events : [];
  return events.map((e) => {
    const x = e as Record<string, unknown>;
    return {
      id: String(x.id ?? ""),
      definition_code: String(x.definition_code ?? x.definition ?? ""),
      action: String(x.action ?? ""),
      version: Number(x.version),
      occurred_at: String(x.occurred_at ?? ""),
      channel: String(x.channel ?? ""),
      proof: x.proof,
    };
  });
}

export async function revokeConsent(
  partyId: string,
  definitionCode: string,
): Promise<void> {
  await consentApi.post(`/api/v1/parties/${encodeURIComponent(partyId)}/revoke`, {
    definition_code: definitionCode,
  });
}
